import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { LoaderDots, Button } from '@reservamos/elements';
import { fieldRenderer } from 'utils/formRenderers';
import shouldShowDiscountCode from 'utils/purchase/isDiscountCodeVisible';
import getPurchaseStepName from '../../../utils/purchase/getPurchaseStepName';
import 'styles/components/purchase/DiscountCode';
import 'styles/components/LoadingDots';
import './DiscountCode.scss';

const propTypes = {
  token: PropTypes.string.isRequired,
  selectedInstallmentsPlan: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  applyingDiscount: PropTypes.bool.isRequired,
  onApplyDiscount: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  features: PropTypes.object.isRequired,
  hasDynamicPricing: PropTypes.bool,
  cdsCouponBlocked: PropTypes.bool,
  purchaseStep: PropTypes.string,
};

const defaultProps = {
  purchaseStep: null,
  cdsCouponBlocked: false,
};

/**
 * DiscountCode component
 * Displays a form to apply discount codes during checkout
 * @param {object} props - token, selectedInstallmentsPlan, handleSubmit, applyingDiscount, onApplyDiscount, t, features, hasDynamicPricing.
 * @param {string} props.token - Authentication token for the purchase.
 * @param {object} props.selectedInstallmentsPlan - The selected payment installments plan.
 * @param {Function} props.handleSubmit - Redux-form submit handler.
 * @param {boolean} props.applyingDiscount - Whether a discount is currently being applied.
 * @param {Function} props.onApplyDiscount - Callback to apply a discount code.
 * @param {Function} props.t - Translation function from i18next.
 * @param {object} props.features - Feature flags from whitelabel configuration.
 * @param {boolean} props.hasDynamicPricing - Whether the route uses dynamic pricing. Coupon input is hidden only when this is true and the feature flag DISCOUNT_CODE_SKIP_ON_EXISTING_DISCOUNT is also enabled.
 * @param {string} props.purchaseStep - Step to evaluate visibility against. Callers that render outside their own route pass it explicitly; everyone else lets the pathname decide.
 */
export class DiscountCode extends Component {
  constructor(props) {
    super(props);

    this.applyDiscount = this.applyDiscount.bind(this);
  }

  /**
   * Applies a discount code to the purchase
   * @param {Object} formValues - Form values containing the discount code
   * @param {string} formValues.discountCode - The discount code to apply
   */
  applyDiscount({ discountCode }) {
    const { token, selectedInstallmentsPlan, onApplyDiscount, cdsCouponBlocked } = this.props;
    if (cdsCouponBlocked) return;

    onApplyDiscount(token, discountCode, selectedInstallmentsPlan);
  }

  /**
   * Determines whether the discount code form should be visible.
   * Falls back to the pathname when the caller did not declare a step. The Quick Purchase modal has
   * to declare one: it renders the quick checkout while the browser is still on search results.
   * @returns {boolean} True when the step, the tenant feature and the dynamic-pricing rule all allow it.
   */
  isDiscountCodeVisible() {
    const { features, hasDynamicPricing, purchaseStep } = this.props;

    return shouldShowDiscountCode({
      purchaseStep: purchaseStep || getPurchaseStepName(window.location.pathname),
      features,
      hasDynamicPricing,
    });
  }

  render() {
    const { handleSubmit, applyingDiscount, cdsCouponBlocked, t } = this.props;

    if (!this.isDiscountCodeVisible()) return null;

    return (
      <div className="discount-code">
        <p className="checkout-text">{t('text.discount_code')}</p>

        <form
          className="form-item-wrap inline-items"
          aria-label={cdsCouponBlocked ? t('payment:cds_points_coupon_blocked') : undefined}
          onSubmit={handleSubmit(this.applyDiscount)}
        >
          <div className="form-item-wrap-l">
            <Field
              type="text"
              name="discountCode"
              id="discountCodeSidebar"
              placeholder={t('payment:placeholder.discount_coupon')}
              placeholderOnly
              cls="form-item textfield"
              component={fieldRenderer}
              isDisabled={cdsCouponBlocked}
            />
          </div>

          <div className="form-item-wrap-button">
            {applyingDiscount ? (
              <LoaderDots isAccent />
            ) : (
              <Button
                withHeight
                text={t('button.apply')}
                buttonType="submit"
                isDisabled={cdsCouponBlocked}
              />
            )}
          </div>
        </form>
      </div>
    );
  }
}

DiscountCode.propTypes = propTypes;
DiscountCode.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
});

export default connect(mapStateToProps)(withTranslation('general')(DiscountCode));
